import { isLandingPageValue, SIGNATURE_FONT_KEYS, } from '@govinity/shared-config';
import { z } from 'zod';
import { addressOptionalSchema } from '../addresses/index.js';
import { phoneOptionalSchema } from '../phones/index.js';
// Base user create validation and DTO type
const userCreateSchema = z.object({
    email: z.email('Invalid email address').transform((val) => val?.toLowerCase()),
    externalId: z.string(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    lastLoginAt: z.iso.datetime().nullish(),
    avatarUrl: z.url('Invalid avatar URL').nullish(),
    address: addressOptionalSchema.nullish(),
    phone: phoneOptionalSchema.nullish(),
});
// Base user update validation and DTO type
export const serviceUserUpdateSchema = userCreateSchema
    .partial()
    .omit({
    email: true,
})
    .extend({
    firstName: z.string('First name is required').min(1, 'First name is required').optional(),
    lastName: z.string('Last name is required').min(1, 'Last name is required').optional(),
});
// Gov user create validation and DTO type
export const govUserCreateSchema = userCreateSchema.extend({
    firstName: z.string('First name is required').min(1, 'First name is required'),
    lastName: z.string('Last name is required').min(1, 'Last name is required'),
    roles: z.array(z.number()).optional(),
    governmentMembership: z.object({
        departmentId: z.number('Department is required'),
        backupUserId: z.number().nullish(),
        title: z.string().nullish(),
    }, 'Government membership data is required'),
});
// Citizen user create validation and DTO type
export const citizenUserCreateSchema = userCreateSchema;
// Gov user invite validation and DTO type
export const govUserInviteSchema = govUserCreateSchema.omit({
    externalId: true,
});
// Gov user update validation and DTO type
export const govUserUpdateSchema = govUserInviteSchema
    .partial()
    .omit({
    email: true,
})
    .extend({
    governmentMembership: govUserInviteSchema.shape.governmentMembership
        .extend({
        blockInternalAccess: z
            .boolean()
            .optional()
            .describe('Blocks the user from the internal app and from being assigned. Member App and public portal access is unaffected.'),
    })
        .optional(),
});
// `key` is the S3 object the upload landed on; the save route verifies it sits under the
// caller's own prefix.
export const userSignatureSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('typed'),
        name: z.string().trim().min(1, 'Type your name to generate a signature'),
        font: z.enum(SIGNATURE_FONT_KEYS),
    }),
    z.object({
        kind: z.literal('image'),
        key: z.string().min(1, 'Upload a signature image'),
    }),
]);
export const toStoredSignature = (value) => userSignatureSchema.safeParse(value).data ?? null;
export const userMeUpdateSchema = serviceUserUpdateSchema
    .pick({
    firstName: true,
    lastName: true,
    avatarUrl: true,
})
    .extend({
    address: addressOptionalSchema.nullish(),
    phone: phoneOptionalSchema.nullish(),
    title: z.string().nullish(),
    // Validated against the landing page catalog but typed as a plain string, so a value stored
    // before an option was renamed or retired still reads back — see `UserResponse.landingPage`.
    landingPage: z
        .string()
        .refine(isLandingPageValue, 'Invalid landing page')
        .nullish()
        .describe('Page the internal app sends this user to on sign-in, as a landing page option value (e.g. "meetings:upcoming", "reports"). Null restores the default.'),
    signature: userSignatureSchema.nullish(),
});
export const userEmailUpdateSchema = userCreateSchema.pick({
    email: true,
});
/**
 * Type guard to check if a user is a government user.
 * Returns true if the user has a governmentId property that is not null or undefined.
 */
export const isGovUser = (user) => 'governmentId' in user && typeof user.governmentId === 'number';
